// Caches the current user's admin role lookup. Uses staff.is_admin() RPC
// (SECURITY DEFINER) so it works for any authenticated user.
//
// Returns:
//   - isAdmin: true | false  (after load)
//   - loading: true while the RPC is in-flight
//   - error:   set if the RPC fails
//
// One fetch per mount. Components needing admin gating call this once at the
// top of the page and branch on the result.

import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

interface UseIsAdminResult {
  isAdmin: boolean;
  loading: boolean;
  error: string | null;
}

export function useIsAdmin(): UseIsAdminResult {
  const [state, setState] = useState<UseIsAdminResult>({
    isAdmin: false,
    loading: true,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    // staff.is_admin() is SECURITY DEFINER and granted to authenticated.
    // Calling .rpc on the staff schema requires .schema('staff') first.
    //
    // The PostgREST request goes through the supabase auth client, which
    // calls `_getUser()` internally to validate the access token. In dev
    // that auto-refresh occasionally races with mount and rejects with
    // `TypeError: Failed to fetch`. Wrap in try/catch (Promise.resolve()
    // converts the rpc's PromiseLike to a real Promise) so the error
    // surfaces in `state.error` rather than as an unhandled rejection.
    (async () => {
      try {
        const { data, error } = await Promise.resolve(
          supabase.schema("staff").rpc("is_admin"),
        );
        if (cancelled) return;
        if (error) {
          setState({ isAdmin: false, loading: false, error: error.message });
          return;
        }
        setState({ isAdmin: Boolean(data), loading: false, error: null });
      } catch (err: unknown) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        setState({ isAdmin: false, loading: false, error: msg });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return state;
}
